import { useRotationStore } from 'src/context/RotationContext';
import { useStowageStore } from 'src/context/StowageContext';
import { useStowageRuleStore } from 'src/context/StowageRuleContext';
import { useVesselStore } from 'src/context/VesselContext';
import { updateManifest } from 'src/utils/stowage/manifest/cargoManifestUtils';
import { mapProducts } from 'src/utils/stowage/productUtils';
import {
    isCargoAfterFirstVoyageOrcaWebStowageImosSync,
    isPortCallAfterFirstVoyageOrcaWebStowageImosSync,
    mapRotation,
    reconcileSequenceChangeFlags,
} from 'src/utils/stowage/stowageRotationUtils';
import { compareRotations } from 'src/utils/stowage/stowagePlanComparison/compareRotationUtilsV2';
import { syncImosGuids } from 'src/utils/stowage/stowagePlanComparison/imosToStowageReferenceUtils';
import { StowagePlan } from 'src/types/ViewModel/Stowage/StowagePlan';
import { RotationPortCall } from 'src/types/ViewModel/Stowage/Rotation/RotationPortCall';
import { StowagePlanTypeEnum } from 'src/types/ViewModel/Stowage/Enums/StowagePlanTypeEnum';
import { deepCopyStowagePlan } from 'src/factory/Stowage/StowagePlanFactory';
import { RotationCargo } from 'src/types/ViewModel/Stowage/Rotation/RotationCargo';
import { Violation } from 'src/types/ViewModel/Stowage/Violation/Violation';
import { useAppStore } from 'src/context/AppContext';
import { useAuthStore } from 'src/context/AuthContext';
import { hasPermission } from 'src/utils/stowage/permissions/stowagePermissionsUtils';
import { stowageFeatureEnum } from 'src/enums/stowage/stowageFeatureEnum';
import useStowageQueryParamsUtils from 'src/utils/stowage/useStowageQueryParamsUtils';
import { updateStowageDataSimple } from 'src/utils/stowage/updateStowageDataUtils';
import { PortCallStatusEnum } from 'src/enums/stowage/portCallStatusEnum';
import React from 'react';
import StowagePlanManuallyEditedMessage from 'src/components/stowage/common/StowagePlanManuallyEditedMessage';

const useUpdateStowagePlan = () => {
    const { selectedVessel } = useVesselStore();
    const { stowageRulesMaps } = useStowageRuleStore();
    const { portNameList, berthNameList, productList, productCategoryList, setStowageRotationTree, imosRotationPortCallList, imosRotationCargoList } =
        useRotationStore();
    const { setAppModalData } = useAppStore();
    const {
        setManifest,
        setIsStowagePlanUpdated,
        setImosSyncChanges,
        setMasterPlanSyncData,
        masterPlanSyncData,
        setHasClosedOldStowagePlanModal,
        hasClosedOldStowagePlanModal,
    } = useStowageStore();
    const { role } = useAuthStore();
    const { setQueryParamStowagePlan, clearQueryParamsStowagePlan } = useStowageQueryParamsUtils();

    const compareWithMaster = async (stowagePlan: StowagePlan, stowagePlans: StowagePlan[]) => {
        if (!stowagePlan.parentGuid) return;

        const currentMasterPlan = stowagePlans.find((sp) => !sp.isDeleted && sp.type === StowagePlanTypeEnum.Master);

        if (!stowagePlan || !currentMasterPlan || !stowagePlan.parentGuid) return;
        if (stowagePlan.masterPlanVersion !== currentMasterPlan.masterPlanVersion) {
            const parentPlan = stowagePlans.find((sp) => sp.stowagePlanGuid === stowagePlan.parentGuid);
            if (!parentPlan) return;

            if (!hasClosedOldStowagePlanModal) {
                const warningMsg = hasPermission(role, stowageFeatureEnum.Promote)
                    ? "This is an old stowage plan. It's safe to view, but promoting it will overwrite the current master plan with outdated data and could lead to incorrect and inconsistent stowage information. Are you sure you want to open it? "
                    : hasPermission(role, stowageFeatureEnum.Publish)
                    ? "This is an old stowage plan. It's safe to view, but publishing it could lead to incorrect and inconsistent stowage information. Are you sure you want to open it?"
                    : "This is an old stowage plan. It's safe to view, but be aware that stowage information may be incorrect and inconsistent. Are you sure you want to open it?";

                setAppModalData({
                    title: 'Old stowage plan',
                    message: warningMsg,
                    actionButtonText: 'Open',
                    action: () => {
                        setHasClosedOldStowagePlanModal(true);
                        setAppModalData(null);
                    },
                    cancel: () => {
                        currentMasterPlan.stowagePlanGuid
                            ? setQueryParamStowagePlan(currentMasterPlan.stowagePlanGuid)
                            : clearQueryParamsStowagePlan();
                        setAppModalData(null);
                    },
                });
            }
        } else if (masterPlanSyncData?.syncChanges.length > 0) {
            setMasterPlanSyncData(null);
        }
    };

    const checkForManualRotationChanges = (stowagePlan: StowagePlan) => {
        if (!hasPermission(role, stowageFeatureEnum.SynchronizeWithImos) || stowagePlan.type !== StowagePlanTypeEnum.Master) {
            return;
        }
        const manualRotation = deepCopyStowagePlan(stowagePlan).rotation.filter((pc) => {
            pc.berthCalls = [...pc.berthCalls].filter((bc) => bc.hasChangedSequence);
            return pc.hasChangedSequence || pc.berthCalls.length > 0;
        });

        if (manualRotation.length > 0) {
            setAppModalData({
                title: 'Rotation changes have been detected in this promoted plan',
                message: React.createElement(StowagePlanManuallyEditedMessage, { portCalls: manualRotation }),
                action: () => {
                    setAppModalData(null);
                },
                isCloseDisabled: true,
            });
        }

        return manualRotation;
    };

    const updateStowageData = async (
        stowagePlan: StowagePlan,
        stowagePlans: StowagePlan[],
        stowagePlanRotationPortCallList: RotationPortCall[],
        stowagePlanRotationCargoList: RotationCargo[],
        violations: Violation[]
    ) => {
        if (!imosRotationPortCallList) return;

        syncImosGuids(imosRotationPortCallList, stowagePlanRotationPortCallList, imosRotationCargoList, stowagePlanRotationCargoList);
        const rotation = stowagePlanRotationPortCallList ?? imosRotationPortCallList ?? [];
        const cargoList = stowagePlanRotationCargoList ?? imosRotationCargoList ?? [];
        mapProducts(productList, productCategoryList, stowageRulesMaps);
        mapRotation(stowagePlan, rotation, cargoList, portNameList, berthNameList, productList, selectedVessel, violations);

        // After mapping rotation with existing + manual changes, reconcile sequence flags against current IMOS snapshot.
        // If the IMOS order already reflects the manual order, clear hasChangedSequence so user doesn't see stale marker.
        try {
            if (imosRotationPortCallList?.length) {
                reconcileSequenceChangeFlags(rotation, imosRotationPortCallList);
            }
        } catch (err) {
            // Fail-safe: don't break update flow due to reconciliation issues.
            // eslint-disable-next-line no-console
            console.warn('reconcileSequenceChangeFlags failed', err);
        }

        setStowageRotationTree([...rotation]);
        updateManifest(stowagePlan, selectedVessel);

        const stowagePlanChanges = compareRotations({
            stowagePlanGuid: stowagePlan.stowagePlanGuid,
            stowagePlanTransitOperations: stowagePlan.transitOperations,
            existingRotation: rotation.filter((pc) =>
                isPortCallAfterFirstVoyageOrcaWebStowageImosSync(pc, selectedVessel.firstVoyageOrcaWebStowageImosSync)
            ),
            incomingRotation: imosRotationPortCallList.filter((pc) =>
                isPortCallAfterFirstVoyageOrcaWebStowageImosSync(pc, selectedVessel.firstVoyageOrcaWebStowageImosSync)
            ),
            existingCargoes: cargoList.filter((c) =>
                isCargoAfterFirstVoyageOrcaWebStowageImosSync(c, selectedVessel.firstVoyageOrcaWebStowageImosSync)
            ),
            incomingCargoes: imosRotationCargoList.filter((c) =>
                isCargoAfterFirstVoyageOrcaWebStowageImosSync(c, selectedVessel.firstVoyageOrcaWebStowageImosSync)
            ),
        });
        setImosSyncChanges([...stowagePlanChanges]);

        await compareWithMaster(stowagePlan, stowagePlans);
        checkForManualRotationChanges(stowagePlan);

        setManifest(stowagePlan.manifest);
        stowagePlan.rotation = stowagePlan.rotation.filter((pc) => pc.portCallStatus !== PortCallStatusEnum.Hidden);
        setIsStowagePlanUpdated({ ...{ value: true } });
    };

    return {
        updateStowageData,
        updateStowageDataSimple,
    };
};
export default useUpdateStowagePlan;
